import {
  localStorageHelper,
  LocalStorageKeys,
} from "@src/helper/localStorage.helper";
import { removeHubspotConversationScript } from "@src/loadHubspotConversation";
import { ConnectedAxios } from "./ConnectedAxios";
import { GatewaySessionApi } from "./session.api";

export type UserSessionData =
  | ConnectedUserSessionData
  | DisconnectedUserSessionData;

export type ConnectedUserSessionData = {
  isConnected: true;
  hasDoubleAuth: boolean | null;
};

export type DisconnectedUserSessionData = {
  isConnected: false;
  hasDoubleAuth: boolean | null;
};

export function isConnectedSessionData(
  sessionData: UserSessionData
): sessionData is ConnectedUserSessionData {
  return sessionData.isConnected;
}

export class UserSession {
  private _onSetSessionData?: (session: UserSessionData) => void;

  private _connectedAxios: ConnectedAxios;

  private _sessionData: UserSessionData = {
    isConnected: false,
    hasDoubleAuth: false,
  };

  private _initializationPromise: Promise<void>;

  constructor() {
    this._connectedAxios = new ConnectedAxios(this);
    this._initializationPromise = this.initializeSession().catch((e) => {
      console.error("Failed to initialize session:", e);
    });
  }

  private async initializeSession() {
    try {
      const sessionStatus = await GatewaySessionApi.getSessionStatus();
      if (sessionStatus.roles)
        localStorageHelper.setItem(
          LocalStorageKeys.ROLES,
          JSON.stringify(sessionStatus.roles)
        );
      this._sessionData = {
        isConnected: sessionStatus.isConnected,
        hasDoubleAuth: sessionStatus.hasDoubleAuth,
      };
      localStorageHelper.setItem(
        LocalStorageKeys.HAS_DOUBLE_AUTH,
        String(sessionStatus.hasDoubleAuth)
      );
      document.body.setAttribute(
        "data-connected",
        String(sessionStatus.isConnected)
      );

      if (this._onSetSessionData) {
        this._onSetSessionData(this._sessionData);
      }
    } catch (error) {
      // If session check fails, assume disconnected
      this._sessionData = {
        isConnected: false,
        hasDoubleAuth: null,
      };
      document.body.setAttribute("data-connected", "false");

      if (this._onSetSessionData) {
        this._onSetSessionData(this._sessionData);
      }
    }
  }

  get initializationPromise() {
    return this._initializationPromise;
  }

  set onSetSessionData(
    onSetSessionData: ((session: UserSessionData) => void) | undefined
  ) {
    this._onSetSessionData = onSetSessionData;
  }

  get onSetSessionData() {
    return this._onSetSessionData;
  }

  get sessionData() {
    return this._sessionData;
  }

  set sessionData(sessionData: UserSessionData) {
    this._sessionData = sessionData;
    if (this.onSetSessionData) this.onSetSessionData(sessionData);
    document.body.setAttribute(
      "data-connected",
      String(sessionData.isConnected)
    );
  }

  get axiosInstance() {
    return this._connectedAxios.axiosApiInstance;
  }

  async login(email: string, password: string) {
    const response = await GatewaySessionApi.signin({ email, password });
    localStorageHelper.setItem(
      LocalStorageKeys.ROLES,
      JSON.stringify(response.roles)
    );
    this.sessionData = {
      isConnected: response.isConnected,
      hasDoubleAuth: response.hasDoubleAuth ?? false,
    };
  }

  async logout() {
    removeHubspotConversationScript();

    try {
      await GatewaySessionApi.logout({});
    } finally {
      this.sessionData = {
        isConnected: false,
        hasDoubleAuth: null,
      };
    }
  }

  async refresh() {
    try {
      const response = await GatewaySessionApi.refresh({});
      localStorageHelper.setItem(
        LocalStorageKeys.ROLES,
        JSON.stringify(response.roles)
      );

      this.sessionData = {
        isConnected: response.isConnected,
        hasDoubleAuth: this.sessionData.hasDoubleAuth,
      };
    } catch (err) {
      await this.logout();
    }
  }
}

export const userSession = new UserSession();
